import { apiClient } from '@consentropy/shared';
import { API_PATHS } from '@consentropy/shared';
import type { ApiListResponse, ApiItemResponse } from '@consentropy/shared';
import type {
  DecisionArea,
  CreateDecisionAreaInput,
  UpdateDecisionAreaInput,
} from '@consentropy/shared';

const DELETED_DECISION_AREAS_KEY = 'consentropy.deletedDecisionAreas';
const DELETED_DECISION_AREAS_TTL_MS = 10 * 60 * 1000;
const CREATED_DECISION_AREAS_KEY = 'consentropy.createdDecisionAreas';
const CREATED_DECISION_AREAS_TTL_MS = 10 * 60 * 1000;
const UPDATED_DECISION_AREAS_KEY = 'consentropy.updatedDecisionAreas';
const UPDATED_DECISION_AREAS_TTL_MS = 10 * 60 * 1000;

type DecisionAreaDeletionTombstone = {
  projectId: string;
  deletedAt: number;
};

type PersistedCreatedDecisionArea = {
  decisionArea: DecisionArea;
  createdAt: number;
};

type PersistedUpdatedDecisionArea = {
  decisionArea: DecisionArea;
  updatedAt: number;
};

function getStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function readDeletedDecisionAreas(): Record<string, DecisionAreaDeletionTombstone> {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(DELETED_DECISION_AREAS_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, DecisionAreaDeletionTombstone>;
    const now = Date.now();
    const active = Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => now - value.deletedAt < DELETED_DECISION_AREAS_TTL_MS),
    ) as Record<string, DecisionAreaDeletionTombstone>;

    if (Object.keys(active).length !== Object.keys(parsed).length) {
      writeDeletedDecisionAreas(active);
    }

    return active;
  } catch {
    try {
      storage.removeItem(DELETED_DECISION_AREAS_KEY);
    } catch {
      // Ignore storage cleanup failures for this fallback layer.
    }
    return {};
  }
}

function writeDeletedDecisionAreas(
  tombstones: Record<string, DecisionAreaDeletionTombstone>,
): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (Object.keys(tombstones).length === 0) {
      storage.removeItem(DELETED_DECISION_AREAS_KEY);
      return;
    }

    storage.setItem(DELETED_DECISION_AREAS_KEY, JSON.stringify(tombstones));
  } catch {
    // This fallback should never break the main data flow if storage is unavailable.
  }
}

function readCreatedDecisionAreas(): Record<string, PersistedCreatedDecisionArea> {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(CREATED_DECISION_AREAS_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, PersistedCreatedDecisionArea>;
    const now = Date.now();
    const active = Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => now - value.createdAt < CREATED_DECISION_AREAS_TTL_MS),
    ) as Record<string, PersistedCreatedDecisionArea>;

    if (Object.keys(active).length !== Object.keys(parsed).length) {
      writeCreatedDecisionAreas(active);
    }

    return active;
  } catch {
    try {
      storage.removeItem(CREATED_DECISION_AREAS_KEY);
    } catch {
      // Ignore storage cleanup failures for this fallback layer.
    }
    return {};
  }
}

function writeCreatedDecisionAreas(
  decisionAreas: Record<string, PersistedCreatedDecisionArea>,
): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (Object.keys(decisionAreas).length === 0) {
      storage.removeItem(CREATED_DECISION_AREAS_KEY);
      return;
    }

    storage.setItem(CREATED_DECISION_AREAS_KEY, JSON.stringify(decisionAreas));
  } catch {
    // This fallback should never break the main data flow if storage is unavailable.
  }
}

function readUpdatedDecisionAreas(): Record<string, PersistedUpdatedDecisionArea> {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(UPDATED_DECISION_AREAS_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, PersistedUpdatedDecisionArea>;
    const now = Date.now();
    const active = Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => now - value.updatedAt < UPDATED_DECISION_AREAS_TTL_MS),
    ) as Record<string, PersistedUpdatedDecisionArea>;

    if (Object.keys(active).length !== Object.keys(parsed).length) {
      writeUpdatedDecisionAreas(active);
    }

    return active;
  } catch {
    try {
      storage.removeItem(UPDATED_DECISION_AREAS_KEY);
    } catch {
      // Ignore storage cleanup failures for this fallback layer.
    }
    return {};
  }
}

function writeUpdatedDecisionAreas(
  decisionAreas: Record<string, PersistedUpdatedDecisionArea>,
): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (Object.keys(decisionAreas).length === 0) {
      storage.removeItem(UPDATED_DECISION_AREAS_KEY);
      return;
    }

    storage.setItem(UPDATED_DECISION_AREAS_KEY, JSON.stringify(decisionAreas));
  } catch {
    // This fallback should never break the main data flow if storage is unavailable.
  }
}

export function saveCreatedDecisionArea(decisionArea: DecisionArea): void {
  const createdDecisionAreas = readCreatedDecisionAreas();
  createdDecisionAreas[decisionArea.id] = {
    decisionArea,
    createdAt: Date.now(),
  };
  writeCreatedDecisionAreas(createdDecisionAreas);
}

export function saveUpdatedDecisionArea(decisionArea: DecisionArea): void {
  const updatedDecisionAreas = readUpdatedDecisionAreas();
  updatedDecisionAreas[decisionArea.id] = {
    decisionArea,
    updatedAt: Date.now(),
  };
  writeUpdatedDecisionAreas(updatedDecisionAreas);
}

export function saveDecisionAreaDeletionTombstone(id: string, projectId: string): void {
  const tombstones = readDeletedDecisionAreas();
  tombstones[id] = {
    projectId,
    deletedAt: Date.now(),
  };
  writeDeletedDecisionAreas(tombstones);

  const createdDecisionAreas = readCreatedDecisionAreas();
  if (createdDecisionAreas[id]) {
    delete createdDecisionAreas[id];
    writeCreatedDecisionAreas(createdDecisionAreas);
  }

  const updatedDecisionAreas = readUpdatedDecisionAreas();
  if (updatedDecisionAreas[id]) {
    delete updatedDecisionAreas[id];
    writeUpdatedDecisionAreas(updatedDecisionAreas);
  }
}

export function clearDecisionAreaDeletionTombstone(id: string): void {
  const tombstones = readDeletedDecisionAreas();
  if (!tombstones[id]) return;

  delete tombstones[id];
  writeDeletedDecisionAreas(tombstones);
}

function filterDeletedDecisionAreas(decisionAreas: DecisionArea[], projectId: string): DecisionArea[] {
  const tombstones = readDeletedDecisionAreas();
  let changed = false;

  const filtered = decisionAreas.filter((decisionArea) => {
    const tombstone = tombstones[decisionArea.id];
    const shouldHide = tombstone?.projectId === projectId;

    if (!shouldHide && tombstone) {
      delete tombstones[decisionArea.id];
      changed = true;
    }

    return !shouldHide;
  });

  if (changed) {
    writeDeletedDecisionAreas(tombstones);
  }

  return filtered;
}

function mergeCreatedDecisionAreas(decisionAreas: DecisionArea[], projectId: string): DecisionArea[] {
  const createdDecisionAreas = readCreatedDecisionAreas();
  const merged = new Map(decisionAreas.map((decisionArea) => [decisionArea.id, decisionArea]));

  for (const [decisionAreaId, value] of Object.entries(createdDecisionAreas)) {
    const createdDecisionArea = value.decisionArea;
    if (createdDecisionArea.projectId !== projectId) continue;

    if (merged.has(decisionAreaId)) {
      // Keep the fallback until its TTL expires. Different API instances can
      // return an older cached list after one instance has returned this area.
      continue;
    }

    merged.set(decisionAreaId, createdDecisionArea);
  }

  return Array.from(merged.values());
}

function mergeUpdatedDecisionAreas(decisionAreas: DecisionArea[], projectId: string): DecisionArea[] {
  const updatedDecisionAreas = readUpdatedDecisionAreas();
  let changed = false;
  const merged = new Map(decisionAreas.map((decisionArea) => [decisionArea.id, decisionArea]));

  for (const [decisionAreaId, value] of Object.entries(updatedDecisionAreas)) {
    const updatedDecisionArea = value.decisionArea;
    if (updatedDecisionArea.projectId !== projectId) continue;

    const fetchedDecisionArea = merged.get(decisionAreaId);
    if (fetchedDecisionArea && JSON.stringify(fetchedDecisionArea) === JSON.stringify(updatedDecisionArea)) {
      delete updatedDecisionAreas[decisionAreaId];
      changed = true;
      continue;
    }

    merged.set(decisionAreaId, updatedDecisionArea);
  }

  if (changed) {
    writeUpdatedDecisionAreas(updatedDecisionAreas);
  }

  return Array.from(merged.values());
}

export async function getDecisionAreasByProject(projectId: string): Promise<DecisionArea[]> {
  const response = await apiClient.get<ApiListResponse<DecisionArea>>(API_PATHS.decisionAreas);
  return mergeUpdatedDecisionAreas(
    mergeCreatedDecisionAreas(
      filterDeletedDecisionAreas(
        response.items.filter((da) => da.projectId === projectId),
        projectId,
      ),
      projectId,
    ),
    projectId,
  );
}

export async function getDecisionAreaById(id: string): Promise<DecisionArea> {
  const response = await apiClient.get<ApiItemResponse<DecisionArea>>(
    `${API_PATHS.decisionAreas}${id}`,
  );
  return response.item;
}

export async function createDecisionArea(input: CreateDecisionAreaInput): Promise<DecisionArea> {
  const response = await apiClient.post<ApiItemResponse<DecisionArea>>(API_PATHS.decisionAreas, input);
  return response.item;
}

export async function updateDecisionArea(id: string, input: UpdateDecisionAreaInput): Promise<DecisionArea> {
  const response = await apiClient.patch<ApiItemResponse<DecisionArea>>(
    `${API_PATHS.decisionAreas}${id}`,
    input,
  );
  return response.item;
}

export async function deleteDecisionArea(id: string): Promise<void> {
  await apiClient.delete(`${API_PATHS.decisionAreas}${id}`);
}
